import { create } from 'zustand';
import { getDesktopApi } from '@renderer/lib/desktop-api';
import { WebConsoleApiError } from '@renderer/lib/web-console-api';
import type {
  AppInfo,
  AppSettings,
  AppSettingsUpdate,
  BootStatus,
  UserPreferences,
  UserPreferencesUpdate
} from '@shared/types';

const defaultSettings: AppSettings = {
  locale: 'ro-RO'
};

const defaultPreferences: UserPreferences = {
  navigationDensity: 'compact',
  reduceMotion: false,
  sidebarCollapsed: false,
  sidebarHidden: false,
  collectionColumnVisibility: {},
  inlineQuickAddExpanded: {},
  projectsViewMode: 'grid',
  leadsViewMode: 'list',
  customersViewMode: 'list',
  resourcesViewMode: 'list',
  contractsViewMode: 'list',
  invoicesViewMode: 'list',
  paymentsViewMode: 'list',
  tasksViewMode: 'kanban'
};

interface ShellStore {
  appInfo: AppInfo | null;
  bootStatus: BootStatus | null;
  settings: AppSettings;
  preferences: UserPreferences;
  routeDetailTitle: string | null;
  editorSaveHandler: (() => void | Promise<void>) | null;
  isBootstrapping: boolean;
  error: string | null;
  errorStatus: number | null;
  load: () => Promise<void>;
  resetBootstrap: () => void;
  clearBootstrapError: () => void;
  saveSettings: (update: AppSettingsUpdate) => Promise<void>;
  savePreferences: (update: UserPreferencesUpdate) => Promise<void>;
  setRouteDetailTitle: (title: string | null) => void;
  setEditorSaveHandler: (handler: (() => void | Promise<void>) | null) => void;
}

export const useShellStore = create<ShellStore>((set, get) => ({
  appInfo: null,
  bootStatus: null,
  settings: defaultSettings,
  preferences: defaultPreferences,
  routeDetailTitle: null,
  editorSaveHandler: null,
  isBootstrapping: true,
  error: null,
  errorStatus: null,
  load: async () => {
    set({
      isBootstrapping: true,
      error: null,
      errorStatus: null
    });

    try {
      const desktopApi = getDesktopApi();
      const [appInfo, bootStatus] = await Promise.all([
        desktopApi.system.getAppInfo(),
        desktopApi.system.getBootStatus()
      ]);

      if (!bootStatus.database.ready) {
        set({
          appInfo,
          bootStatus,
          settings: defaultSettings,
          preferences: defaultPreferences,
          isBootstrapping: false,
          error: null,
          errorStatus: null
        });
        return;
      }

      const [settings, preferences] = await Promise.all([
        desktopApi.settings.getAppSettings(),
        desktopApi.preferences.getUserPreferences()
      ]);

      set({
        appInfo,
        bootStatus,
        settings,
        preferences,
        isBootstrapping: false,
        error: null,
        errorStatus: null
      });
    } catch (error) {
      set({
        isBootstrapping: false,
        error: error instanceof Error ? error.message : 'Renderer bootstrap failed.',
        errorStatus: error instanceof WebConsoleApiError ? error.status : null
      });
    }
  },
  resetBootstrap: () =>
    set({
      appInfo: null,
      bootStatus: null,
      isBootstrapping: true,
      error: null,
      errorStatus: null
    }),
  clearBootstrapError: () =>
    set({
      error: null,
      errorStatus: null
    }),
  saveSettings: async (update) => {
    const settings = await getDesktopApi().settings.updateAppSettings(update);
    set({ settings });
  },
  savePreferences: async (update) => {
    const previous = get().preferences;
    const optimisticPreferences: UserPreferences = {
      navigationDensity: update.navigationDensity ?? previous.navigationDensity,
      reduceMotion: update.reduceMotion ?? previous.reduceMotion,
      sidebarCollapsed: update.sidebarCollapsed ?? previous.sidebarCollapsed,
      sidebarHidden: update.sidebarHidden ?? previous.sidebarHidden,
      collectionColumnVisibility: update.collectionColumnVisibility ?? previous.collectionColumnVisibility,
      inlineQuickAddExpanded: update.inlineQuickAddExpanded ?? previous.inlineQuickAddExpanded,
      projectsViewMode: update.projectsViewMode ?? previous.projectsViewMode,
      leadsViewMode: update.leadsViewMode ?? previous.leadsViewMode,
      customersViewMode: update.customersViewMode ?? previous.customersViewMode,
      resourcesViewMode: update.resourcesViewMode ?? previous.resourcesViewMode,
      contractsViewMode: update.contractsViewMode ?? previous.contractsViewMode,
      invoicesViewMode: update.invoicesViewMode ?? previous.invoicesViewMode,
      paymentsViewMode: update.paymentsViewMode ?? previous.paymentsViewMode,
      tasksViewMode: update.tasksViewMode ?? previous.tasksViewMode
    };
    set({ preferences: optimisticPreferences });

    try {
      const preferences = await getDesktopApi().preferences.updateUserPreferences(update);
      set({ preferences });
    } catch (error) {
      set({
        preferences: previous,
        error: error instanceof Error ? error.message : 'Preferences could not be persisted.',
        errorStatus: error instanceof WebConsoleApiError ? error.status : null
      });
    }
  },
  setRouteDetailTitle: (routeDetailTitle) => set({ routeDetailTitle }),
  setEditorSaveHandler: (editorSaveHandler) => set({ editorSaveHandler })
}));
